import { useState, useEffect } from 'react';
import { client } from '../../sdkAdapter';
import Swal from 'sweetalert2';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import { Modal } from "../ui/modal";
import MediaUploader from "../common/MediaUploader";
import Pagination from '../ui/pagination';

interface InvoiceSetting {
    id: number;
    name: string;
    currency_code: string;
    currency_symbol: string;
    currency_format: string;
    notes: string;
    active: boolean | number;
    image_id: number | null;
}

export default function InvoiceSettingsTable() {
    const [settings, setSettings] = useState<InvoiceSetting[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<InvoiceSetting | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    const [formData, setFormData] = useState({
        name: '',
        currency_code: 'AED',
        currency_symbol: 'د.إ',
        currency_format: '{SYMBOL}{VALUE}',
        notes: 'Thank you.',
        active: true,
        image_id: undefined as number | undefined,
    });

    const refreshData = async () => {
        setLoading(true);
        try {
            const res = await client.getApiInvoicesSettings({}, {
                params: {
                    page: currentPage,
                    per_page: rowsPerPage
                }
            }) as any;
            let data: InvoiceSetting[] = [];
            // Response structure: res.data = { status, data: paginatedObj }
            // paginatedObj = { current_page, data: [...items...], ... }
            const payload = res.data?.data;
            if (payload?.data) {
                data = Array.isArray(payload.data) ? payload.data : [payload.data];
                setTotalItems(payload.total || payload.data.length || 0);
            } else if (Array.isArray(payload)) {
                data = payload;
                setTotalItems(payload.length);
            } else if (Array.isArray(res.data)) {
                data = res.data;
                setTotalItems(res.data.length);
            } else {
                setTotalItems(0);
            }
            setSettings(data);
        } catch (err) {
            console.error('Failed to fetch invoice settings:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refreshData();
    }, [currentPage, rowsPerPage]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleAddClick = () => {
        setEditingItem(null);
        setFormData({
            name: '',
            currency_code: 'AED',
            currency_symbol: 'د.إ',
            currency_format: '{SYMBOL}{VALUE}',
            notes: 'Thank you.',
            active: true,
            image_id: undefined,
        });
        setIsModalOpen(true);
    };

    const handleEditClick = (item: InvoiceSetting) => {
        setEditingItem(item);
        setFormData({
            name: item.name,
            currency_code: item.currency_code || 'AED',
            currency_symbol: item.currency_symbol || 'د.إ',
            currency_format: item.currency_format || '{SYMBOL}{VALUE}',
            notes: item.notes || '',
            active: !!item.active,
            image_id: item.image_id || undefined,
        });
        setIsModalOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const payload: any = {
                name: formData.name,
                currency_code: formData.currency_code,
                currency_symbol: formData.currency_symbol,
                currency_format: formData.currency_format,
                notes: formData.notes,
                active: formData.active,
            };
            if (formData.image_id) {
                payload.image_id = formData.image_id;
            }

            if (editingItem) {
                await client.postApiInvoicesSettingsUpdate({
                    postApiInvoicesSettingsUpdateRequest: {
                        id: editingItem.id,
                        ...payload,
                    }
                });
            } else {
                await client.postApiInvoicesSettingsCreate({
                    postApiInvoicesSettingsCreateRequest: payload,
                });
            }
            setIsModalOpen(false);
            await Swal.fire({
                title: 'Success',
                text: `Invoice setting ${editingItem ? 'updated' : 'created'} successfully.`,
                icon: 'success'
            });
            refreshData();
        } catch (err) {
            console.error('Failed to save invoice setting:', err);
            await Swal.fire({
                title: 'Error',
                text: 'Failed to save invoice setting',
                icon: 'error'
            });
        }
    };

    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (!result.isConfirmed) return;

        try {
            await client.postApiInvoicesSettingsDelete({
                postApiInvoicesSettingsDeleteRequest: { id }
            });
            await Swal.fire({
                title: 'Deleted!',
                text: 'Invoice setting deleted successfully.',
                icon: 'success'
            });
            if (settings.length === 1 && currentPage > 1) {
                setCurrentPage(prev => prev - 1);
            } else {
                refreshData();
            }
        } catch (err) {
            console.error('Failed to delete invoice setting:', err);
            await Swal.fire({
                title: 'Error',
                text: 'Failed to delete invoice setting',
                icon: 'error'
            });
        }
    };

    const totalPages = Math.ceil(totalItems / rowsPerPage);

    return (
        <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Invoice Settings</h3>
                <div className="flex items-center gap-2">
                    {loading && <span className="text-sm text-gray-500">Loading...</span>}
                    <button
                        onClick={handleAddClick}
                        className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors text-sm flex items-center gap-2"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Setting
                    </button>
                </div>
            </div>
            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableCell isHeader className="px-5 py-3 text-start">Name</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Currency</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Format</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Notes</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Status</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-end">Actions</TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {settings.length === 0 ? (
                            <TableRow><TableCell colSpan={6} className="px-5 py-4 text-center">No invoice settings found.</TableCell></TableRow>
                        ) : (
                            settings.map(item => (
                                <TableRow key={item.id}>
                                    <TableCell className="px-5 py-4 font-medium">{item.name}</TableCell>
                                    <TableCell className="px-5 py-4">
                                        <span className="text-gray-600 dark:text-gray-300">
                                            {item.currency_code} <span className="text-gray-400">({item.currency_symbol})</span>
                                        </span>
                                    </TableCell>
                                    <TableCell className="px-5 py-4">
                                        <code className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">{item.currency_format}</code>
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-gray-500 max-w-xs truncate">{item.notes}</TableCell>
                                    <TableCell className="px-5 py-4">
                                        {item.active ? (
                                            <span className="text-green-600 font-medium text-xs bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded">Active</span>
                                        ) : (
                                            <span className="text-gray-500 font-medium text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">Inactive</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-end">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => handleEditClick(item)}
                                                className="text-gray-500 hover:text-brand-500 dark:text-gray-400 dark:hover:text-brand-400"
                                                title="Edit"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => handleDelete(item.id)}
                                                className="text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400"
                                                title="Delete"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {totalItems > 0 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(rows) => {
                        setRowsPerPage(rows);
                        setCurrentPage(1);
                    }}
                    totalItems={totalItems}
                />
            )}

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className="max-w-lg p-0 overflow-hidden">
                <div className="flex flex-col h-full max-h-[90vh]">
                    <div className="p-6 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 sticky top-0 z-10">
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white">{editingItem ? 'Edit Invoice Setting' : 'Add Invoice Setting'}</h3>
                    </div>

                    <div className="p-6 overflow-y-auto flex-grow">
                        <form id="invoice-settings-form" onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Invoice Logo</label>
                                <MediaUploader
                                    onUpload={(ids) => setFormData(prev => ({ ...prev, image_id: ids[0] }))}
                                    initialImages={formData.image_id ? [formData.image_id] : []}
                                    multiple={false}
                                    folder="banners"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Name *</label>
                                <input
                                    type="text"
                                    name="name"
                                    required
                                    maxLength={50}
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                                    placeholder="e.g. Invoice"
                                />
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Currency Code</label>
                                    <input
                                        type="text"
                                        name="currency_code"
                                        maxLength={5}
                                        value={formData.currency_code}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                                        placeholder="AED"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Currency Symbol</label>
                                    <input
                                        type="text"
                                        name="currency_symbol"
                                        maxLength={3}
                                        value={formData.currency_symbol}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                                        placeholder="د.إ"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Currency Format</label>
                                    <input
                                        type="text"
                                        name="currency_format"
                                        maxLength={50}
                                        value={formData.currency_format}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                                        placeholder="{SYMBOL}{VALUE}"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Notes</label>
                                <textarea
                                    name="notes"
                                    maxLength={255}
                                    value={formData.notes}
                                    onChange={handleInputChange}
                                    rows={3}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                                    placeholder="Thank you for your purchase."
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    name="active"
                                    id="invoice-setting-active"
                                    checked={formData.active}
                                    onChange={handleInputChange}
                                    className="w-4 h-4 rounded border-gray-300"
                                />
                                <label htmlFor="invoice-setting-active" className="text-sm font-medium">Active</label>
                            </div>
                        </form>
                    </div>

                    <div className="p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-white/[0.02] sticky bottom-0 z-10 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => setIsModalOpen(false)}
                            className="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="invoice-settings-form"
                            className="px-6 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 font-medium shadow-md shadow-brand-500/20 transition-all active:scale-95"
                        >
                            {editingItem ? 'Save Changes' : 'Add'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
